// Leute und Geräte (E3, S7) – was die Seite rechnet, ohne React: je Rolle
// ein Satz, was sie sieht; welche Geräte zu wem gehören; und was der
// Angemeldete an einer Person tun darf.
//
// Die Sätze stehen nur HIER (eine Quelle für „Jemanden einladen“, „Rolle
// ändern“ und „Wer sieht was“). Was eine Rolle wirklich darf, steht im Kern
// (rechte.ts); der Test prüft, dass jeder Satz dazu passt – nennt ein Satz
// die Freigabe, muss die Rolle freigeben dürfen, und umgekehrt.
//
// Was der Angemeldete an einer Person tun darf, folgt den Regeln am Server
// (server/src/anmeldung/routen.ts): Leute verwalten Leitung und Büro; was
// einen Zugang der Leitung betrifft und jede Rolle ändert nur die Leitung;
// sich selbst sperrt und stuft niemand um, und das eigene Gerät sperrt man
// nicht selbst. Die Oberfläche bietet nur an, was dann auch geht.

import { darf, darfDoku, VERGEBBARE_ROLLEN, type GeraetZeile, type NutzerZeile, type Rolle } from "@happy-energy/kern";
import type { IkonName } from "@happy-energy/oberflaeche";

/** Was eine Rolle sieht – ein Satz, ohne Erklärung verständlich. */
export const ROLLEN_SAETZE: Record<Rolle, string> = {
  leitung: "Sieht alles und darf alles – auch Rollen vergeben. Dazu die Doku-App am Handy.",
  buero: "Übersicht, alle Besichtigungen und die Freigabe, Kunden, Bestellung und Partien, Standardmaterial, Leute und Geräte – dazu Unterlagen und Fragen.",
  verkauf: "Die Doku-App am Handy; im CRM nur die eigenen Besichtigungen und Fragen.",
  // Seit E3c nicht mehr zu vergeben – es gibt kein Lager mehr (H26); der Satz bleibt für einen übrig gebliebenen Zugang.
  lager: "Gibt es nicht mehr – bitte eine andere Rolle geben.",
  montage: "Die Unterlagen für die Montage und die Fragen.",
  inbetriebnahme: "Wie die Montage: die Unterlagen und die Fragen – die eigene Kiste kommt später.",
};

/** Ein Zeichen je Rolle. */
export const ROLLEN_IKON: Record<Rolle, IkonName> = {
  leitung: "ziel",
  buero: "firma",
  verkauf: "kamera",
  lager: "paket",
  montage: "werkzeug",
  inbetriebnahme: "blitz",
};

/** Wo jemand mit dieser Rolle den Einladungscode eintippt – der Verkauf am Handy, die Leitung an beiden, alle anderen im CRM. */
export function woAnmelden(rolle: Rolle): string {
  if (!darfDoku(rolle)) return "im CRM am Laptop";
  return rolle === "verkauf" ? "in der Doku-App am Handy" : "in der Doku-App am Handy oder im CRM am Laptop";
}

/** Welche Rollen der Angemeldete vergeben darf: alle, die man vergeben kann (seit E3c ohne Lager) – die Leitung nur, wer selbst Rollen vergibt. */
export function rollenZurWahl(ichRolle: Rolle): Rolle[] {
  return VERGEBBARE_ROLLEN.filter((r) => r !== "leitung" || darf(ichRolle, "rollen-vergeben"));
}

// --- Was ich an einer Person tun darf ------------------------------------------------------------

export interface PersonRechte {
  /** Neuer Einladungscode (nur für aktive Zugänge). */
  neuerCode: boolean;
  /** Die Rolle ändern – nur die Leitung, nie bei sich selbst. */
  rolleAendern: boolean;
  /** Austreten lassen (= sperren) – nie sich selbst. */
  austreten: boolean;
  /** Ein ausgetretener Zugang kommt wieder dazu. */
  wiederAufnehmen: boolean;
  /** Ihre Geräte freigeben oder sperren. */
  geraete: boolean;
  /** Warum nichts geht, obwohl man Leute verwalten darf – sonst null. */
  grund: string | null;
}

export function personRechte(ich: { id: string; rolle: Rolle }, person: Pick<NutzerZeile, "id" | "rolle" | "aktiv">): PersonRechte {
  const verwalten = darf(ich.rolle, "leute");
  const leitungGeschuetzt = person.rolle === "leitung" && !darf(ich.rolle, "rollen-vergeben");
  const darfHier = verwalten && !leitungGeschuetzt;
  const selbst = person.id === ich.id;
  return {
    neuerCode: darfHier && person.aktiv,
    rolleAendern: darf(ich.rolle, "rollen-vergeben") && !selbst && person.aktiv,
    austreten: darfHier && !selbst && person.aktiv,
    wiederAufnehmen: darfHier && !selbst && !person.aktiv,
    geraete: darfHier,
    grund: verwalten && leitungGeschuetzt ? "Zugänge der Leitung ändert nur die Leitung." : null,
  };
}

/** Darf ich dieses Gerät sperren? Nicht das, an dem ich gerade sitze (der Server weist es ohnehin ab). */
export function geraetSperrbar(ichGeraetId: string, g: Pick<GeraetZeile, "id" | "stand">): boolean {
  return g.stand !== "gesperrt" && g.id !== ichGeraetId;
}

// --- Geräte zu den Leuten -----------------------------------------------------------------------

const STAND_REIHE: Record<GeraetZeile["stand"], number> = { wartet: 0, freigegeben: 1, gesperrt: 2 };

function zeitWert(iso: string | null): number {
  const t = iso ? new Date(iso).getTime() : Number.NaN;
  return Number.isNaN(t) ? 0 : t;
}

/** Geräte in der Reihenfolge, in der man sie braucht: wartende zuerst, dann freigegebene, gesperrte zuletzt – je zuletzt gesehen zuerst. */
export function geraeteOrdnen<G extends Pick<GeraetZeile, "stand" | "zuletztGesehen" | "angelegtAm">>(geraete: readonly G[]): G[] {
  return [...geraete].sort(
    (a, b) => STAND_REIHE[a.stand] - STAND_REIHE[b.stand] || zeitWert(b.zuletztGesehen ?? b.angelegtAm) - zeitWert(a.zuletztGesehen ?? a.angelegtAm),
  );
}

/** Je Person ihre Geräte (geordnet) – eine Person ohne Gerät bekommt eine leere Liste. Geräte von Unbekannten bleiben unter `ohnePerson`. */
export function geraeteJePerson(geraete: readonly GeraetZeile[], leute: readonly Pick<NutzerZeile, "id">[]): { jePerson: Map<string, GeraetZeile[]>; ohnePerson: GeraetZeile[] } {
  const jePerson = new Map<string, GeraetZeile[]>(leute.map((n) => [n.id, []]));
  const ohnePerson: GeraetZeile[] = [];
  for (const g of geraete) {
    const liste = jePerson.get(g.nutzer.id);
    if (liste) liste.push(g);
    else ohnePerson.push(g);
  }
  for (const [id, liste] of jePerson) jePerson.set(id, geraeteOrdnen(liste));
  return { jePerson, ohnePerson: geraeteOrdnen(ohnePerson) };
}

const nachName = new Intl.Collator("de", { sensitivity: "base" });

/** Die Leute in der Reihenfolge der Seite: ich zuerst, dann die aktiven nach Namen, die ausgetretenen zuletzt. */
export function leuteOrdnen<N extends Pick<NutzerZeile, "id" | "name" | "aktiv">>(leute: readonly N[], ichId: string): N[] {
  return [...leute].sort(
    (a, b) => Number(b.id === ichId) - Number(a.id === ichId) || Number(b.aktiv) - Number(a.aktiv) || nachName.compare(a.name, b.name),
  );
}
